import React, { useEffect, useMemo, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api, apiErrors } from "@/lib/api";
import { formatDate, formatINR, todayStr } from "@/lib/format";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  PhoneCall, Minus, Plus, Save, AlertCircle, CheckCircle2, Trash2, History, Lock, Users2, CalendarDays, Pencil,
} from "lucide-react";

function Stepper({ label, value, onChange, step = 1, disabled, hint }) {
  const set = (v) => onChange(Math.max(0, v));
  const key = String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-semibold text-slate-200 flex items-center justify-between">
        <span>{label}</span>
        {hint && <span className="text-[11px] font-normal text-slate-500">{hint}</span>}
      </label>
      <div className="flex items-stretch rounded-xl border border-white/10 overflow-hidden bg-white/5 focus-within:ring-2 focus-within:ring-[#C9A24B]/40 focus-within:border-[#C9A24B]/50">
        <button type="button" data-testid={`dec-${key}`} disabled={disabled} onClick={() => set((Number(value) || 0) - step)}
          className="px-3 bg-white/5 hover:bg-white/10 text-slate-400 disabled:opacity-40 transition-colors"><Minus className="h-4 w-4" /></button>
        <input data-testid={`input-${key}`} type="number" disabled={disabled}
          value={value === 0 ? 0 : value} onChange={(e) => set(parseInt(e.target.value || "0", 10))}
          className="flex-1 w-full text-center font-mono font-bold text-lg text-white bg-transparent outline-none disabled:text-slate-600" />
        <button type="button" data-testid={`inc-${key}`} disabled={disabled} onClick={() => set((Number(value) || 0) + step)}
          className="px-3 bg-white/5 hover:bg-white/10 text-[#C9A24B] disabled:opacity-40 transition-colors"><Plus className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

function MoneyInput({ label, value, onChange, disabled }) {
  const key = String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-semibold text-slate-200">{label}</label>
      <div className="flex items-center rounded-xl border border-white/10 bg-white/5 overflow-hidden focus-within:ring-2 focus-within:ring-[#C9A24B]/40 focus-within:border-[#C9A24B]/50">
        <span className="px-3 text-slate-500 font-mono">₹</span>
        <input data-testid={`input-${key}`} type="number" disabled={disabled}
          value={value === 0 ? 0 : value} onChange={(e) => onChange(Math.max(0, parseInt(e.target.value || "0", 10)))}
          className="flex-1 w-full py-2.5 pr-3 font-mono font-bold text-lg text-white bg-transparent outline-none disabled:text-slate-600" />
      </div>
    </div>
  );
}

const EMPTY = {
  attendance: "Working", dialed: 0, connected: 0, leads: 0, followups_talked: 0, crm_updated: 0,
  online_consultations: 0, not_interested_leads: 0,
  new_visits: 0, old_visits: 0, referral_visits: 0, referral_name: "", referral_source: "", referral_offer: "",
  treated_new: 0, treated_old: 0, revenue: 0, new_revenue: 0, old_revenue: 0, collected: 0, due: 0,
  no_treatment_reasons: [], objection_note: "", learning_link: "",
};

export default function DailyEntry() {
  const { activeEmployees, telecallers, settings, currentUser } = useApp();
  const defaultEmp = currentUser?.role === "telecaller" ? currentUser.id : (telecallers[0]?.id || activeEmployees[0]?.id);
  const [empId, setEmpId] = useState(defaultEmp);
  const [date, setDate] = useState(todayStr());
  const [form, setForm] = useState(EMPTY);
  const [existingId, setExistingId] = useState(null);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!empId) return;
    let active = true;
    api.getEntry(empId, date).then((e) => {
      if (!active) return;
      if (e) { setForm({ ...EMPTY, ...e }); setExistingId(e.id); }
      else { setForm(EMPTY); setExistingId(null); }
      setErrors([]); setShowAudit(false);
    });
    return () => { active = false; };
  }, [empId, date]);

  const totalVisits = (form.new_visits || 0) + (form.old_visits || 0);
  const totalTreated = (form.treated_new || 0) + (form.treated_old || 0);
  const notTreated = Math.max(0, totalVisits - totalTreated);
  const connectRate = form.dialed ? Math.round((form.connected / form.dialed) * 1000) / 10 : 0;
  const isWorking = form.attendance === "Working";

  useEffect(() => {
    if (!isWorking) return;
    setForm((f) => {
      const rows = [...(f.no_treatment_reasons || [])];
      if (rows.length < notTreated) { while (rows.length < notTreated) rows.push({ patient: "", type: "new", reason: "", note: "" }); }
      else if (rows.length > notTreated) { rows.length = notTreated; }
      else return f;
      return { ...f, no_treatment_reasons: rows };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notTreated, isWorking]);

  const lockDays = settings?.edit_lock_days ?? 7;
  const dayDiff = Math.floor((new Date(todayStr()) - new Date(date)) / 86400000);
  const privileged = currentUser?.role === "owner" || currentUser?.role === "manager";
  const locked = existingId && !privileged && dayDiff > lockDays;

  const clientErrors = useMemo(() => {
    if (!isWorking) return [];
    const errs = [];
    if (form.connected > form.dialed) errs.push("Connected calls cannot exceed dialed calls");
    if (form.not_interested_leads > form.leads) errs.push("Not-interested leads cannot exceed total leads");
    if (form.treated_new > form.new_visits) errs.push("New treatments cannot exceed new visits");
    if (form.treated_old > form.old_visits) errs.push("Old treatments cannot exceed old visits");
    if (form.referral_visits > totalVisits) errs.push("Referral visits cannot exceed total visits");
    const filledRows = (form.no_treatment_reasons || []).filter((r) => r.reason).length;
    if (notTreated > 0 && filledRows < notTreated) errs.push(`Select a reason for all ${notTreated} not-treated patient(s)`);
    return errs;
  }, [form, isWorking, totalVisits, notTreated]);

  const save = async () => {
    setErrors([]);
    if (clientErrors.length) { setErrors(clientErrors); toast.error("Please fix the highlighted errors"); return; }
    setSaving(true);
    try {
      const payload = { ...form, employee_id: empId, date, changed_by: currentUser?.name };
      const saved = await api.saveEntry(payload);
      setExistingId(saved.id);
      toast.success(existingId ? "Entry updated" : "Entry saved");
    } catch (err) {
      setErrors(apiErrors(err)); toast.error("Could not save entry");
    } finally { setSaving(false); }
  };

  const loadAudit = async () => { if (!existingId) return; setAudit(await api.auditLog(existingId)); setShowAudit(true); };
  const doDelete = async () => { if (!existingId) return; await api.deleteEntry(existingId, currentUser?.name); setForm(EMPTY); setExistingId(null); toast.success("Entry deleted"); };

  const reasonList = settings?.no_treatment_reasons || [];
  const refSources = settings?.referral_sources || [];
  const attendanceList = settings?.attendance_statuses || ["Working", "Week Off", "Leave", "Half Day"];

  return (
    <div className="pb-28 max-w-3xl mx-auto animate-fade-up">
      <div className="surface p-5 mb-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-11 w-11 rounded-xl bg-[#C9A24B]/15 border border-[#C9A24B]/25 flex items-center justify-center">
            <PhoneCall className="h-5 w-5 text-[#C9A24B]" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-light text-white">Daily Call Entry</h2>
            <p className="text-sm text-slate-400">Pick a date (back-dates allowed) · edit anytime within the lock window</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-slate-200 flex items-center gap-1.5"><Users2 className="h-3.5 w-3.5" /> Telecaller</label>
            <Select value={empId} onValueChange={setEmpId}>
              <SelectTrigger data-testid="entry-employee-select" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{activeEmployees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-slate-200 flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> Date</label>
            <Input data-testid="entry-date-input" type="date" max={todayStr()} value={date} onChange={(e) => setDate(e.target.value)} className="h-11 rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-slate-200">Attendance</label>
            <Select value={form.attendance} onValueChange={(v) => upd({ attendance: v })}>
              <SelectTrigger data-testid="entry-attendance-select" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{attendanceList.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3 text-xs">
          <span className="text-slate-500">{formatDate(date)}</span>
          {existingId ? (
            <span className="inline-flex items-center gap-1 text-emerald-400 font-semibold"><Pencil className="h-3.5 w-3.5" /> Editing saved entry</span>
          ) : (<span className="text-slate-500">New entry</span>)}
          {locked && <span className="inline-flex items-center gap-1 text-rose-400 font-semibold"><Lock className="h-3.5 w-3.5" /> Locked (edit window {lockDays}d passed)</span>}
        </div>
      </div>

      {!isWorking ? (
        <div className="rounded-2xl p-6 text-center bg-amber-500/10 border border-amber-500/30 text-amber-300">
          <AlertCircle className="h-7 w-7 mx-auto mb-2" />
          <div className="font-semibold">Marked as {form.attendance}</div>
          <div className="text-sm text-amber-400/80 mt-1">Numeric fields are not required and will be saved as 0.</div>
        </div>
      ) : (
        <fieldset disabled={locked} className="space-y-5">
          <Section title="A · Calls">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Stepper label="Total Dial Calls" value={form.dialed} onChange={(v) => upd({ dialed: v })} step={5} />
              <Stepper label="Connected Calls" value={form.connected} onChange={(v) => upd({ connected: v })} />
              <Stepper label="Leads Received" value={form.leads} onChange={(v) => upd({ leads: v })} />
              <Stepper label="Follow-ups Talked" value={form.followups_talked} onChange={(v) => upd({ followups_talked: v })} />
              <Stepper label="CRM Updated" value={form.crm_updated} onChange={(v) => upd({ crm_updated: v })} />
              <Stepper label="Online Consultations" value={form.online_consultations} onChange={(v) => upd({ online_consultations: v })} />
              <Stepper label="Not-Interested Leads" value={form.not_interested_leads} onChange={(v) => upd({ not_interested_leads: v })} hint={`≤ ${form.leads}`} />
              <div className="flex flex-col justify-end">
                <div className="rounded-xl bg-[#C9A24B]/10 border border-[#C9A24B]/25 px-3 py-2">
                  <div className="text-[11px] font-semibold text-[#C9A24B] uppercase">Connect Rate</div>
                  <div className="font-mono font-bold text-lg text-[#C9A24B]">{connectRate}%</div>
                </div>
              </div>
            </div>
          </Section>

          <Section title="B · Patient Visits">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Stepper label="New Patient Visits" value={form.new_visits} onChange={(v) => upd({ new_visits: v })} />
              <Stepper label="Old Patient Visits" value={form.old_visits} onChange={(v) => upd({ old_visits: v })} />
              <div className="flex flex-col justify-end">
                <div className="rounded-xl bg-indigo-500/10 border border-indigo-500/25 px-3 py-2">
                  <div className="text-[11px] font-semibold text-indigo-300 uppercase">Total Visits</div>
                  <div className="font-mono font-bold text-lg text-indigo-200">{totalVisits}</div>
                </div>
              </div>
              <Stepper label="New Took Treatment" value={form.treated_new} onChange={(v) => upd({ treated_new: v })} hint={`≤ ${form.new_visits}`} />
              <Stepper label="Old Took Treatment" value={form.treated_old} onChange={(v) => upd({ treated_old: v })} hint={`≤ ${form.old_visits}`} />
              <Stepper label="Referral Visits" value={form.referral_visits} onChange={(v) => upd({ referral_visits: v })} />
            </div>
            {form.referral_visits > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                <Input data-testid="referral-name" placeholder="Referral name (optional)" value={form.referral_name} onChange={(e) => upd({ referral_name: e.target.value })} className="rounded-xl" />
                <Select value={form.referral_source || undefined} onValueChange={(v) => upd({ referral_source: v })}>
                  <SelectTrigger data-testid="referral-source" className="rounded-xl"><SelectValue placeholder="Referral source" /></SelectTrigger>
                  <SelectContent>{refSources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid="referral-offer" placeholder="Offer % (optional)" value={form.referral_offer} onChange={(e) => upd({ referral_offer: e.target.value })} className="rounded-xl" />
              </div>
            )}
          </Section>

          <Section title="C · Revenue">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <MoneyInput label="Total Revenue Today" value={form.revenue} onChange={(v) => upd({ revenue: v })} />
              <div className="flex flex-col justify-end">
                <button type="button" onClick={() => upd({ revenue: (form.new_revenue || 0) + (form.old_revenue || 0) })}
                  className="text-xs text-[#C9A24B] font-semibold hover:underline self-start mb-1" data-testid="auto-sum-revenue">Auto-sum from split ↓</button>
              </div>
              <MoneyInput label="New Patient Revenue" value={form.new_revenue} onChange={(v) => upd({ new_revenue: v })} />
              <MoneyInput label="Old Patient Revenue" value={form.old_revenue} onChange={(v) => upd({ old_revenue: v })} />
              <MoneyInput label="Collected" value={form.collected} onChange={(v) => upd({ collected: v })} />
              <MoneyInput label="Due" value={form.due} onChange={(v) => upd({ due: v })} />
            </div>
          </Section>

          {notTreated > 0 && (
            <Section title={`D · Reasons for No Treatment (${notTreated} required)`}>
              <div className="space-y-3">
                {(form.no_treatment_reasons || []).map((row, i) => (
                  <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center surface-2 p-3" data-testid={`reason-row-${i}`}>
                    <Input placeholder="Patient (optional)" value={row.patient} onChange={(e) => updateReason(setForm, i, { patient: e.target.value })} className="sm:col-span-3 rounded-lg" data-testid={`reason-patient-${i}`} />
                    <Select value={row.type} onValueChange={(v) => updateReason(setForm, i, { type: v })}>
                      <SelectTrigger className="sm:col-span-2 rounded-lg" data-testid={`reason-type-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="new">New</SelectItem><SelectItem value="old">Old</SelectItem></SelectContent>
                    </Select>
                    <Select value={row.reason || undefined} onValueChange={(v) => updateReason(setForm, i, { reason: v })}>
                      <SelectTrigger className="sm:col-span-3 rounded-lg" data-testid={`reason-select-${i}`}><SelectValue placeholder="Reason" /></SelectTrigger>
                      <SelectContent>{reasonList.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input placeholder="Note (optional)" value={row.note} onChange={(e) => updateReason(setForm, i, { note: e.target.value })} className="sm:col-span-4 rounded-lg" data-testid={`reason-note-${i}`} />
                  </div>
                ))}
              </div>
            </Section>
          )}

          <Section title="E · Notes (optional)">
            <div className="grid grid-cols-1 gap-3">
              <Textarea placeholder="Objection handled / learnings…" value={form.objection_note} onChange={(e) => upd({ objection_note: e.target.value })} className="rounded-xl" data-testid="objection-note" />
              <Input placeholder="Learning video / objection handling link" value={form.learning_link} onChange={(e) => upd({ learning_link: e.target.value })} className="rounded-xl" data-testid="learning-link" />
            </div>
          </Section>
        </fieldset>
      )}

      {errors.length > 0 && (
        <div className="mt-5 rounded-xl p-4 bg-rose-500/10 border border-rose-500/30" data-testid="entry-errors">
          <div className="flex items-center gap-2 text-rose-300 font-semibold text-sm mb-1"><AlertCircle className="h-4 w-4" /> Please fix:</div>
          <ul className="list-disc list-inside text-sm text-rose-300/90 space-y-0.5">{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}

      {showAudit && (
        <div className="mt-5 surface p-4" data-testid="audit-log">
          <div className="font-heading font-semibold text-white mb-2 flex items-center gap-2"><History className="h-4 w-4" /> Audit Log</div>
          {audit.length === 0 ? <div className="text-sm text-slate-500">No changes recorded.</div> : (
            <div className="space-y-2 max-h-60 overflow-y-auto scrollbar-thin">
              {audit.map((a) => (
                <div key={a.id} className="text-xs text-slate-400 border-b border-white/5 pb-2">
                  <span className="font-semibold text-slate-200">{a.changed_by || "Unknown"}</span> · {new Date(a.timestamp).toLocaleString("en-IN")}
                  <ul className="list-disc list-inside mt-1 text-slate-500">
                    {(a.changes || []).map((c, i) => <li key={i}>{c.field}: {String(c.old)} → {String(c.new)}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 z-30 bg-[#0b1120]/95 backdrop-blur-md border-t border-white/10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="flex-1 flex items-center gap-4 text-xs text-slate-400">
            {isWorking && <><span>Visits <b className="text-white font-mono">{totalVisits}</b></span>
            <span>Treated <b className="text-white font-mono">{totalTreated}</b></span>
            <span className="hidden sm:inline">Revenue <b className="text-white font-mono">{formatINR(form.revenue)}</b></span></>}
          </div>
          {existingId && (
            <>
              <Button variant="outline" size="icon" onClick={loadAudit} data-testid="view-audit-btn" className="rounded-xl"><History className="h-4 w-4" /></Button>
              {!locked && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="icon" className="rounded-xl text-rose-400 border-rose-500/30 hover:bg-rose-500/10" data-testid="delete-entry-btn"><Trash2 className="h-4 w-4" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
                      <AlertDialogDescription>This soft-deletes the entry for {formatDate(date)}. It will be removed from reports but kept in the audit trail.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </>
          )}
          <Button onClick={save} disabled={saving || locked} data-testid="save-entry-btn" className="rounded-xl bg-[#C9A24B] hover:bg-[#b8923f] text-[#0b1120] h-11 px-6 font-semibold">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : existingId ? "Update" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function updateReason(setForm, i, patch) {
  setForm((f) => { const rows = [...(f.no_treatment_reasons || [])]; rows[i] = { ...rows[i], ...patch }; return { ...f, no_treatment_reasons: rows }; });
}

function Section({ title, children }) {
  return (
    <div className="surface p-5 animate-fade-up">
      <div className="text-xs font-bold tracking-wider text-[#C9A24B] uppercase mb-4">{title}</div>
      {children}
    </div>
  );
}
